import React, { useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { ButtonWithIconAndLoader } from '@td-client-frontend/common';
import Modal from '../../Modal/NewModal';
import { ProfileType, useSaveProfile } from '../../../hooks/useMarketingProfile';
import { Button } from '../../Button';
import { AffectedGroups } from '../components/';
import { PendingChangesContext } from '../../../context/PendingChangesContext';

interface Props {
  isOpen: boolean;
  toggleSaveModal: () => void;
  profileData: any;
}

interface SaveModalContentProps {
  profile: {
    id: string;
    name: string;
    type: ProfileType;
  };
  onLoadingStateChange?: (x: boolean) => void;
}

export function SaveModalContent({ profile: { name, id, type }, onLoadingStateChange }: SaveModalContentProps) {
  return (
    <div className="px-7 pb-16 pt-6 text-lg tracking-wider" data-testid="marketing-save-profile-modal">
      <h3 className="mb-3">
        Profile:{' '}
        <span className="ml-2 text-2xl font-medium tracking-wider" data-testid="marketing-save-profile-name">
          {name}
        </span>
      </h3>
      <AffectedGroups id={id} type={type} onLoadingStateChange={onLoadingStateChange} profileName={name} />
      <h4>Are you sure you want to apply these changes for this profile?</h4>
    </div>
  );
}

export function SaveModal({ isOpen, toggleSaveModal, profileData }: Props) {
  const { setIsChanged } = useContext(PendingChangesContext);

  const { saveProfile, isLoading: saveProfileLoading } = useSaveProfile();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleLoadingStateChange = (state: boolean) => setIsLoading(state);

  const cancelButtonHandler = () => {
    if (!saveProfileLoading) toggleSaveModal();
  };

  const handleSave = useCallback(async () => {
    setIsChanged(false);
    const { id } = await saveProfile({
      id: profileData.id,
      name: profileData.name,
      baseFlg: profileData.default,
      settings: profileData.settings
    });
    if (id) {
      toast.success('Profile successfully saved', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
        autoClose: 10000
      });
    }
    toggleSaveModal();
  }, [
    profileData.default,
    profileData.id,
    profileData.name,
    profileData.settings,
    saveProfile,
    setIsChanged,
    toggleSaveModal
  ]);

  const CustomButtons = () => (
    <div className="flex gap-4">
      <Button title="Cancel" kind="secondary" onClick={toggleSaveModal} disabled={saveProfileLoading} />
      <div className="w-28">
        <ButtonWithIconAndLoader
          title="Save"
          loading={saveProfileLoading}
          className="flex items-center justify-center font-effra btn h-full"
          labelClasses={clsx('text-normal', saveProfileLoading && 'hidden')}
          onClick={handleSave}
          disabled={isLoading}
        />
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancelButtonHandler}
      header="Save profile changes"
      size="medium"
      customFooterActions={{
        right: <CustomButtons />
      }}
    >
      {profileData && (
        <SaveModalContent
          profile={{
            id: profileData.id,
            name: profileData.name,
            type: ProfileType.PROFILE
          }}
          onLoadingStateChange={handleLoadingStateChange}
        />
      )}
    </Modal>
  );
}
